import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_URL, timeout: 15000 });

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      sessionStorage.removeItem('token');
      localStorage.removeItem('token');
      if (!['/login', '/register'].includes(window.location.pathname)) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  requestVerification: () => api.post('/auth/request-verification'),
};

export const walletsAPI = {
  list: () => api.get('/wallets'),
  available: () => api.get('/wallets/available'),
  requests: () => api.get('/wallets/requests'),
  requestWallet: (data) => api.post('/wallets/request', data),
  deposit: (data) => api.post('/wallets/deposit', data),
  qrCode: (walletId) => api.get(`/wallets/${walletId}/qr`),
};

export const exchangeAPI = {
  rates: () => api.get('/exchange/rates'),
  swap: (data) => api.post('/exchange/swap', data),
};

export const twoFaAPI = {
  setup: () => api.post('/auth/2fa/setup'),
  enable: (code) => api.post('/auth/2fa/enable', { code }),
  disable: (code) => api.post('/auth/2fa/disable', { code }),
  verify: (code, tempToken) => api.post('/auth/2fa/verify', { code }, { headers: { 'X-2FA-Token': tempToken } }),
};

export const transactionsAPI = {
  list: (params) => api.get('/transactions', { params }),
  send: (data) => api.post('/transactions/send', data),
  details: (id) => api.get(`/transactions/${id}/details`),
};

export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  dismiss: (id) => api.put(`/notifications/${id}/dismiss`),
  markAllRead: () => api.put('/notifications/read-all'),
  dismissAll: () => api.put('/notifications/dismiss-all'),
};

export const passwordChangeAPI = {
  request: (data) => api.post('/password-change/request', data),
  status: () => api.get('/password-change/status'),
};

export const adminAPI = {
  stats: () => api.get('/admin/stats'),
  users: (params) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  transactions: (params) => api.get('/admin/transactions', { params }),
  walletRequests: (params) => api.get('/admin/wallet-requests', { params }),
  approveRequest: (id) => api.put(`/admin/wallet-requests/${id}/approve`),
  rejectRequest: (id) => api.put(`/admin/wallet-requests/${id}/reject`),
  userWallets: (userId) => api.get(`/admin/users/${userId}/wallets`),
  userDetails: (userId) => api.get(`/admin/users/${userId}/details`),
  updateWallet: (walletId, data) => api.put(`/admin/wallets/${walletId}`, data),
  verifications: (params) => api.get('/admin/verifications', { params }),
  approveVerification: (userId) => api.put(`/admin/verifications/${userId}/approve`),
  rejectVerification: (userId) => api.put(`/admin/verifications/${userId}/reject`),
  sendNotification: (data) => api.post('/admin/notifications/send', data),
  notificationsHistory: () => api.get('/admin/notifications/history'),
  changePassword: (userId, data) => api.post(`/admin/change-password/${userId}`, data),
  createTransaction: (data) => api.post('/admin/transactions', data),
  updateTransaction: (txId, data) => api.put(`/admin/transactions/${txId}`, data),
  approveTransaction: (txId) => api.put(`/admin/transactions/${txId}/approve`),
  rejectTransaction: (txId) => api.put(`/admin/transactions/${txId}/reject`),
  deleteTransaction: (txId) => api.delete(`/admin/transactions/${txId}`),
  passwordRequests: () => api.get('/admin/password-requests'),
  approvePasswordRequest: (reqId) => api.put(`/admin/password-requests/${reqId}/approve`),
  rejectPasswordRequest: (reqId) => api.put(`/admin/password-requests/${reqId}/reject`),
  blockUser: (userId) => api.put(`/admin/users/${userId}/block`),
  unblockUser: (userId) => api.put(`/admin/users/${userId}/unblock`),
};

export const currenciesAPI = {
  list: () => api.get('/currencies'),
};

export const creditsAPI = {
  me: () => api.get('/credits/me'),
  apply: (data) => api.post('/credits/apply', data),
};

// Extend admin API with credit management (mutates existing object)
adminAPI.creditApplications = (params) => api.get('/admin/credit-applications', { params });
adminAPI.approveCreditApp = (id) => api.put(`/admin/credit-applications/${id}/approve`);
adminAPI.rejectCreditApp = (id) => api.put(`/admin/credit-applications/${id}/reject`);
adminAPI.setCreditLimit = (userId, credit_limit) => api.put(`/admin/users/${userId}/credit-limit`, { credit_limit });
adminAPI.setCryptoAddresses = (userId, data) => api.put(`/admin/users/${userId}/crypto-addresses`, data);

export default api;
